import { useState, FormEvent } from "react";
import SidebarLayout from "@/components/feature/SidebarLayout";
import { useScrollReveal, getRevealClass } from "@/hooks/useScrollReveal";
import { supabase } from "@/lib/supabase";

const WEB3FORMS_URL = "https://api.web3forms.com/submit";
const WEB3FORMS_KEY = import.meta.env.VITE_WEB3FORMS_ACCESS_KEY || "";

export default function Contact() {
  const [charCount, setCharCount] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorDetail, setErrorDetail] = useState("");
  const { ref, isVisible } = useScrollReveal();

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorDetail("");
    const form = e.currentTarget;
    const formData = new FormData(form);
    const message = String(formData.get("お問い合わせ内容") ?? "");

    if (message.length > 500) {
      alert("お問い合わせ内容は500文字以内で入力してください。");
      return;
    }

    if (!WEB3FORMS_KEY || WEB3FORMS_KEY === "YOUR_ACCESS_KEY_HERE") {
      setSubmitStatus("error");
      setErrorDetail("Web3Formsのアクセスキーが設定されていません。.envファイルの VITE_WEB3FORMS_ACCESS_KEY を設定してください。");
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus("idle");

    const name = String(formData.get("お名前") ?? "");
    const email = String(formData.get("email") ?? "");
    const phone = String(formData.get("電話番号") ?? "");
    const organization = String(formData.get("組織名") ?? "");

    try {
      // 1. Save to Supabase
      const { error: dbError } = await supabase.from("contact_submissions").insert({
        name,
        email,
        phone,
        organization: organization || null,
        message,
      });

      if (dbError) {
        console.error("Supabase insert error:", dbError);
        throw new Error(`データ保存エラー: ${dbError.message}`);
      }

      // 2. Send notification via Web3Forms
      const web3Payload = {
        access_key: WEB3FORMS_KEY,
        subject: "ホームページからのお問い合わせ",
        "お名前": name,
        email,
        "組織名": organization,
        "電話番号": phone,
        "お問い合わせ内容": message,
      };

      const web3Res = await fetch(WEB3FORMS_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(web3Payload),
      });

      const web3Data = await web3Res.json().catch(() => ({ success: false, message: "不明なエラー" }));

      if (!web3Res.ok || !web3Data.success) {
        console.error("Web3Forms error:", web3Data);
        throw new Error(`メール送信エラー: ${web3Data.message || `HTTP ${web3Res.status}`}`);
      }

      setSubmitStatus("success");
      form.reset();
      setCharCount(0);
    } catch (err) {
      setSubmitStatus("error");
      setErrorDetail(err instanceof Error ? err.message : "予期しないエラーが発生しました");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <SidebarLayout breadcrumbs={[{ label: 'お問い合わせ' }]}>

      {/* Hero */}
      <section className="px-4 md:px-6 lg:px-10 mb-10">
        <div className="relative w-full h-[320px] sm:h-[400px] md:h-[500px] lg:h-[580px] overflow-hidden rounded-2xl">
          <img
            src="https://static.readdy.ai/image/8da10c853ee44f0b201c0b84a1169777/0802e14152a43672bce5be2d9507e8ab.webp"
            alt="お問い合わせ"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-black/25" />
          <div ref={ref} className={`absolute inset-0 flex flex-col items-center justify-center z-10 ${getRevealClass(isVisible)}`}>
            <h1
              className="text-4xl sm:text-5xl md:text-6xl font-bold text-white tracking-wide"
              style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
            >
              Contact
            </h1>
            <p
              className="text-sm sm:text-base text-white/90 mt-3 tracking-wider"
              style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
            >
              お問い合わせ
            </p>
          </div>
        </div>
      </section>

      {/* Content Card */}
      <div className="px-4 md:px-6 lg:px-10 pb-16 md:pb-24">
        <div className="bg-white rounded-2xl p-8 md:p-12 lg:p-16">
          <div className="max-w-4xl mx-auto">
            <p
              className="text-sm text-gray-700 leading-relaxed mb-10"
              style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
            >
              弊社タレントへの出演依頼、動画制作のご依頼、かぼす商品に関するお問い合わせはコチラです。
              <br />
              その他のご質問・ご相談もお気軽にお問合せください。
              <br />
              ※お問い合わせいただいた全ての方にご返信することは出来かねます。お問い合わせ内容によってはご返信できないこともありますので予めご了承ください。
            </p>

            {submitStatus === "success" && (
              <div className="mb-8 rounded-xl bg-green-50 border border-green-200 p-6 text-center">
                <p className="text-green-800 font-medium text-base mb-1">お問い合わせを受け付けました</p>
                <p className="text-green-600 text-sm">内容を確認の上、担当者よりご連絡させていただきます。</p>
              </div>
            )}

            {submitStatus === "error" && (
              <div className="mb-8 rounded-xl bg-red-50 border border-red-200 p-6 text-center">
                <p className="text-red-800 font-medium text-base mb-1">送信に失敗しました</p>
                <p className="text-red-600 text-sm">時間をおいて再度お試しください。</p>
                {errorDetail && (
                  <p className="text-red-500 text-xs mt-2 font-mono bg-red-100 rounded px-2 py-1 break-all">{errorDetail}</p>
                )}
              </div>
            )}

            <form
              id="contact-form"
              onSubmit={handleSubmit}
              className="space-y-6"
            >
              {/* Web3Forms 隠しフィールド */}
              <input type="hidden" name="access_key" value={WEB3FORMS_KEY} />
              <input type="hidden" name="subject" value="ホームページからのお問い合わせ" />

              {/* 担当者名 */}
              <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-8">
                <label
                  className="sm:w-40 shrink-0 text-sm text-black pt-3"
                  style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                >
                  担当者名 <span className="text-red-500 text-xs">必須</span>
                </label>
                <input
                  type="text"
                  name="お名前"
                  required
                  placeholder="例：山田太郎"
                  className="flex-1 bg-gray-50 rounded-lg px-4 py-3 text-sm text-black placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-300"
                  style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                />
              </div>

              {/* 組織名、部署名 */}
              <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-8">
                <label
                  className="sm:w-40 shrink-0 text-sm text-black pt-3"
                  style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                >
                  組織名、部署名
                </label>
                <input
                  type="text"
                  name="組織名"
                  placeholder="例：営業部"
                  className="flex-1 bg-gray-50 rounded-lg px-4 py-3 text-sm text-black placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-300"
                  style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                />
              </div>

              {/* メールアドレス */}
              <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-8">
                <label
                  className="sm:w-40 shrink-0 text-sm text-black pt-3"
                  style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                >
                  メールアドレス <span className="text-red-500 text-xs">必須</span>
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="例：mail@example.com"
                  className="flex-1 bg-gray-50 rounded-lg px-4 py-3 text-sm text-black placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-300"
                  style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                />
              </div>

              {/* 電話番号 */}
              <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-8">
                <label
                  className="sm:w-40 shrink-0 text-sm text-black pt-3"
                  style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                >
                  電話番号 <span className="text-red-500 text-xs">必須</span>
                </label>
                <input
                  type="tel"
                  name="電話番号"
                  required
                  placeholder="例：03-1111-4444"
                  className="flex-1 bg-gray-50 rounded-lg px-4 py-3 text-sm text-black placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-300"
                  style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                />
              </div>

              {/* お問い合わせ内容 */}
              <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-8">
                <label
                  className="sm:w-40 shrink-0 text-sm text-black pt-3"
                  style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                >
                  お問い合わせ内容 <span className="text-red-500 text-xs">必須</span>
                </label>
                <div className="flex-1">
                  <textarea
                    name="お問い合わせ内容"
                    required
                    rows={8}
                    maxLength={500}
                    placeholder="例：具体的なお問い合わせ内容をご記入ください"
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full bg-gray-50 rounded-lg px-4 py-3 text-sm text-black placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-300 resize-none"
                    style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                  />
                  <p className={`text-right text-xs mt-1 ${charCount > 500 ? "text-red-400" : "text-gray-400"}`}>
                    {charCount} / 500
                  </p>
                </div>
              </div>

              {/* Privacy Policy */}
              <div className="flex flex-col items-center gap-3 pt-6">
                <a
                  href="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-black underline underline-offset-2 hover:opacity-70 transition-opacity"
                  style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                >
                  プライバシーポリシーを確認
                </a>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="privacy_agreement"
                    required
                    className="w-4 h-4 rounded border-gray-300 text-black focus:ring-black"
                  />
                  <span
                    className="text-sm text-black"
                    style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                  >
                    プライバシーポリシーに同意する
                  </span>
                </label>
              </div>

              <div className="flex justify-center pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting || charCount > 500}
                  className="bg-[#2563eb] text-white text-sm tracking-wider px-12 py-4 rounded-full hover:bg-blue-700 transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap"
                  style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                >
                  {isSubmitting ? "送信中..." : "問い合わせを送信"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </SidebarLayout>
  );
}